'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { usePromotion, useDeletePromotions } from '@/lib/api/promotions'
import { useToast } from '@/components/ui/use-toast'
import { useParams } from 'react-router-dom'

interface DeletePromotionModalProps {
    promotionId?: string
    onClose: () => void
    onSuccess?: () => void
}

interface DeletePromotionModalContentProps {
    promotionId: string
    storeId: string
    onClose: () => void
    onSuccess?: () => void
}

export const DeletePromotionModal = ({ promotionId, onClose, onSuccess }: DeletePromotionModalProps) => {
    const { id } = useParams()
    if (!promotionId || !id) return null
    return <DeletePromotionModalContent promotionId={promotionId} storeId={id} onClose={onClose} onSuccess={onSuccess} />
}

const DeletePromotionModalContent = ({ promotionId, storeId, onClose, onSuccess }: DeletePromotionModalContentProps) => {
    const { t } = useTranslation('stores')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const { data: promotion, isPending: isLoadingPromotion } = usePromotion(promotionId, storeId)
    const deletePromotions = useDeletePromotions()

    const handleDelete = async () => {
        if (!promotionId) return

        try {
            await deletePromotions.mutateAsync({ ids: [promotionId], storeId })

            toast({
                title: t('promotionsTable.deleteSuccess'),
                description: t('promotionsTable.deleteDescription', { name: promotion?.name || t('promotionsTable.fallbackName') }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('promotionsTable.deleteError'),
                variant: 'destructive',
            })
        }
    }

    const promotionName = promotion?.name || t('promotionsTable.fallbackName')

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('promotionsTable.modals.delete.title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {isLoadingPromotion ? t('promotionsTable.modals.delete.loading') : t('promotionsTable.modals.delete.description', { name: promotionName })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deletePromotions.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deletePromotions.isPending || isLoadingPromotion}
                    >
                        {deletePromotions.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

